<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import { useEmailOpenSignals } from 'dashboard/composables/useEmailOpenSignals';
import { useMessageContext } from './provider.js';
import { MESSAGE_TYPES } from './constants';

const context = useMessageContext();
const copy = {
  signal: '打开信号：',
  details: '打开信号详情',
  first: '首次检测：',
  last: '最后检测：',
  note: '仅供参考，不能确认已读',
};
const route = useRoute();
const inboxGetter = useMapGetter('inboxes/getInbox');
const enabled = computed(() => {
  const inbox = inboxGetter.value(context.inboxId.value);
  const labels = inbox?.borderchat_labels || inbox?.borderchatLabels || [];
  return (
    context.isEmailInbox.value &&
    context.messageType.value === MESSAGE_TYPES.OUTGOING &&
    !context.isPrivate.value &&
    !context.contentAttributes.value?.deleted &&
    !labels.some(label => String(label).trim() === '亚马逊邮箱')
  );
});
const signal = useEmailOpenSignals({
  accountId: computed(() => route.params.accountId),
  conversationId: context.conversationId,
  messageId: context.id,
  enabled,
});
const labels = {
  none: '暂无打开信号',
  automatic: '疑似自动加载',
  proxy: '代理加载，无法确认打开',
  unknown: '检测到加载，无法确认打开',
  possible_open: '疑似真人打开',
};
const label = computed(() =>
  signal.value?.expired && signal.value.state === 'none'
    ? '检测期已结束'
    : labels[signal.value?.state] || ''
);
const formatTime = value => {
  const date = new Date(value);
  return value && Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      }).format(date)
    : '—';
};
const explanation = computed(() => {
  const value = signal.value;
  if (!value) return '';
  return [
    '打开信号不代表已读；预加载、缓存和本人查看已发送邮件均可能影响记录。时间为当前设备时区。',
    `检测 ${value.detection_count} 次，其中疑似自动加载 ${value.automatic_count} 次、代理加载 ${value.proxy_count} 次。`,
    value.first_possible_open_at
      ? `首次疑似真人打开：${formatTime(value.first_possible_open_at)}`
      : '',
    value.last_possible_open_at
      ? `最后疑似真人打开：${formatTime(value.last_possible_open_at)}`
      : '',
    value.expired ? '已超过检测期，历史信号保留。' : '',
  ]
    .filter(Boolean)
    .join('\n');
});
</script>

<template>
  <div
    v-if="enabled && signal"
    class="mt-2 max-w-full text-xs leading-5 text-n-slate-11"
    data-email-open-signal
  >
    <Popover :key="context.id.value" align="end">
      <template #default="{ isOpen }">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 p-0 text-xs leading-5 bg-transparent border-0 cursor-pointer rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9 hover:underline"
          :class="
            signal.state === 'possible_open'
              ? 'text-n-blue-11'
              : 'text-n-slate-11'
          "
          aria-haspopup="dialog"
          :aria-expanded="isOpen"
        >
          <span
            class="i-lucide-mail-open size-3.5 shrink-0"
            aria-hidden="true"
          />
          <span>{{ copy.signal }}{{ label }}</span>
        </button>
      </template>
      <template #content>
        <div
          role="dialog"
          :aria-label="copy.details"
          class="w-80 max-w-full p-4 text-xs leading-5 text-n-slate-11"
          data-email-open-signal-details
        >
          <div class="mb-2 font-medium text-n-slate-12">{{ copy.details }}</div>
          <div v-if="signal.first_detected_at">
            {{ copy.first
            }}<time :datetime="signal.first_detected_at">{{
              formatTime(signal.first_detected_at)
            }}</time>
          </div>
          <div v-if="signal.last_detected_at">
            {{ copy.last
            }}<time :datetime="signal.last_detected_at">{{
              formatTime(signal.last_detected_at)
            }}</time>
          </div>
          <div class="mt-2 text-n-slate-10">{{ copy.note }}</div>
          <div class="mt-2 whitespace-pre-line">{{ explanation }}</div>
        </div>
      </template>
    </Popover>
  </div>
  <span v-else class="hidden" />
</template>
